/* Mats · Ficha de producto (sections/mats-product.liquid)
   Depende de window.MatsCart y window.MatsWishlist (assets/mats-product-card.js, cargado en todo el tema).
   Habla con la tabla de medidas (assets/mats-size-chart.js) por eventos:
     lanza  mats:size:change {size}   ·   escucha mats:size:pick {size} */
(() => {
  if (customElements.get('mats-product')) return;

  const norm = (s) => String(s || '').toUpperCase().replace(/\s+/g, '').replace(/^XXL$/, '2XL').replace(/^XXXL$/, '3XL');
  const RECENT_KEY = 'mats:recent';

  class MatsProduct extends HTMLElement {
    connectedCallback() {
      if (this._ready) return;
      this._ready = true;
      try {
        this.data = JSON.parse(this.querySelector('[data-mb-pd-json]').textContent);
      } catch (e) {
        return;
      }
      const d = this.dataset;
      this.variants = this.data.variants || [];
      this.sizeIdx = Number(d.sizeIndex);
      this.colorIdx = Number(d.colorIndex);
      this.lowThreshold = Number(d.lowThreshold || 0);
      this.form = this.querySelector('[data-mb-pd-form]');
      this.idInput = this.querySelector('[data-mb-pd-id]');
      this.addBtn = this.querySelector('[data-mb-pd-add]');
      this.addLabel = this.querySelector('[data-mb-pd-add-label]');
      this.avail = this.querySelector('[data-mb-pd-avail]');
      this.preNote = this.querySelector('[data-mb-pd-preorder]');
      this.groups = {};
      this.querySelectorAll('[data-mb-pd-option]').forEach((el) => { this.groups[Number(el.dataset.mbPdOption)] = el; });

      // Selección inicial: variante de la URL o primera disponible; la talla se deja sin elegir
      const startId = Number(this.idInput ? this.idInput.value : 0);
      const start = this.variants.find((v) => v.id === startId) || this.variants.find((v) => v.available) || this.variants[0];
      this.selected = start ? start.options.slice() : [];
      if (this.sizeIdx > -1 && d.preselect !== 'true') this.selected[this.sizeIdx] = null;

      this.setupGallery();
      this.bindOptions();
      this.bindBuy();
      this.bindBar();
      this.bindGuide();
      this.bindLightbox();
      this.bindWishlist();
      this.update({ user: false, gallery: true });
      this.rememberRecent();
    }

    /* ---------------- Variantes ---------------- */
    matches(v, sel) {
      return sel.every((val, i) => val === null || val === undefined || v.options[i] === val);
    }

    exactVariant() {
      if (this.selected.some((x) => x === null || x === undefined)) return null;
      return this.variants.find((v) => this.matches(v, this.selected)) || null;
    }

    displayVariant() {
      const exact = this.exactVariant();
      if (exact) return exact;
      const list = this.variants.filter((v) => this.matches(v, this.selected));
      return list.find((v) => v.available) || list[0] || this.variants[0];
    }

    bindOptions() {
      Object.entries(this.groups).forEach(([idx, el]) => {
        el.addEventListener('click', (e) => {
          const btn = e.target.closest('[data-value]');
          if (!btn || !el.contains(btn)) return;
          this.select(Number(idx), btn.dataset.value, true);
        });
      });
    }

    select(idx, value, user) {
      const prevColor = this.colorIdx > -1 ? this.selected[this.colorIdx] : null;
      this.selected[idx] = value;
      if (idx === this.sizeIdx) {
        this.groups[idx]?.classList.remove('is-invalid');
        window.dispatchEvent(new CustomEvent('mats:size:change', { detail: { size: value, source: 'product' } }));
      }
      const colorChanged = this.colorIdx > -1 && this.selected[this.colorIdx] !== prevColor;
      this.update({ user, gallery: colorChanged });
    }

    update({ user = false, gallery = false } = {}) {
      const exact = this.exactVariant();
      const shown = this.displayVariant();
      if (!shown) return;

      // Estado de cada botón
      Object.entries(this.groups).forEach(([idxStr, el]) => {
        const idx = Number(idxStr);
        el.querySelectorAll('[data-value]').forEach((btn) => {
          const val = btn.dataset.value;
          btn.setAttribute('aria-pressed', String(this.selected[idx] === val));
          const probe = this.selected.slice();
          probe[idx] = val;
          if (idx === this.colorIdx && this.sizeIdx > -1) probe[this.sizeIdx] = null;
          const candidates = this.variants.filter((v) => this.matches(v, probe));
          const available = candidates.some((v) => v.available);
          btn.classList.toggle('is-soldout', !available);
          if (idx === this.sizeIdx) {
            const v = candidates.length === 1 ? candidates[0] : candidates.find((x) => x.available);
            const low = v && v.available && v.qty !== null && v.qty > 0 && v.qty <= this.lowThreshold;
            btn.classList.toggle('is-low', Boolean(low));
          }
        });
        const label = el.querySelector('[data-mb-pd-opt-value]');
        if (label) label.textContent = this.selected[idx] || '';
      });

      const colorName = this.querySelector('[data-mb-pd-color-name]');
      if (colorName && this.colorIdx > -1) colorName.textContent = this.selected[this.colorIdx] || '';

      // Precio
      const now = this.querySelector('[data-mb-pd-price-now]');
      const was = this.querySelector('[data-mb-pd-price-was]');
      const badge = this.querySelector('[data-mb-pd-price-badge]');
      if (now) { now.textContent = shown.price; now.classList.toggle('is-sale', Boolean(shown.compare)); }
      if (was) { was.textContent = shown.compare || ''; was.hidden = !shown.compare; }
      if (badge) { badge.textContent = shown.discount ? `−${shown.discount}%` : ''; badge.hidden = !shown.compare || !shown.discount; }

      if (this.idInput) this.idInput.value = (exact || shown).id;

      // Disponibilidad y botón
      const d = this.dataset;
      const needSize = this.sizeIdx > -1 && !this.selected[this.sizeIdx];
      const pre = this.isPreorder(exact);
      if (this.preNote) this.preNote.hidden = !pre;
      if (this.avail) {
        this.avail.className = 'mb-pd__avail';
        if (needSize) {
          this.avail.textContent = d.labelInitial;
        } else if (exact && exact.available) {
          if (exact.pre && d.labelPreAvail) {
            this.avail.textContent = d.labelPreAvail;
            this.avail.classList.add('is-pre');
          } else if (exact.qty !== null && exact.qty > 0 && exact.qty <= this.lowThreshold) {
            this.avail.textContent = d.labelLow.replace('[count]', exact.qty);
            this.avail.classList.add('is-low');
          } else {
            this.avail.textContent = d.labelInstock;
            this.avail.classList.add('is-ok');
          }
        } else {
          this.avail.textContent = d.labelSizeSoldout;
          this.avail.classList.add('is-soldout');
        }
      }
      if (this.addBtn) {
        const soldOut = exact && !exact.available;
        this.addBtn.disabled = Boolean(soldOut);
        if (this.addLabel && !this.addBtn.classList.contains('is-added')) this.addLabel.textContent = soldOut ? d.labelSoldout : (pre ? d.labelPreorder : d.labelAdd);
      }

      this.updateBar(exact, shown, needSize);
      if (gallery) this.applyGallery();
      if (user && exact) this.pushUrl(exact.id);
      this.updateGuidePick();
    }

    /* Pre-order: la talla elegida o, sin talla, todas las variantes que encajan con lo elegido */
    isPreorder(exact) {
      if (exact) return Boolean(exact.pre && exact.available);
      const list = this.variants.filter((v) => v.available && this.selected.every((val, i) => !val || v.options[i] === val));
      return list.length > 0 && list.every((v) => v.pre);
    }

    pushUrl(id) {
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', id);
        window.history.replaceState(window.history.state, '', url.toString());
      } catch (e) { /* noop */ }
    }

    /* ---------------- Galería ---------------- */
    setupGallery() {
      this.mediaList = this.querySelector('[data-mb-pd-media]');
      if (!this.mediaList) return;
      this.mediaItems = Array.from(this.mediaList.querySelectorAll('[data-media-id]'));
      const ids = this.mediaItems.map((el) => Number(el.dataset.mediaId));
      this.colorGroups = {};
      if (this.colorIdx > -1) {
        const starts = new Map();
        this.variants.forEach((v) => {
          const c = v.options[this.colorIdx];
          if (v.media && !starts.has(c)) starts.set(c, ids.indexOf(v.media));
        });
        const sorted = Array.from(new Set(Array.from(starts.values()).filter((i) => i > -1))).sort((a, b) => a - b);
        starts.forEach((startIdx, color) => {
          if (startIdx < 0) return;
          const next = sorted.find((i) => i > startIdx);
          this.colorGroups[color] = ids.slice(startIdx, next === undefined ? ids.length : next);
        });
      }

      this.counter = this.querySelector('[data-mb-pd-counter]');
      this.total = this.querySelector('[data-mb-pd-total]');
      this.mediaList.addEventListener('scroll', () => {
        if (!this.counter) return;
        const first = this.visibleMedia()[0];
        if (!first) return;
        const w = first.getBoundingClientRect().width || 1;
        const i = Math.round(this.mediaList.scrollLeft / w) + 1;
        this.counter.textContent = Math.min(i, this.visibleMedia().length);
      }, { passive: true });
    }

    visibleMedia() {
      return (this.mediaItems || []).filter((el) => !el.hidden).sort((a, b) => Number(a.style.order) - Number(b.style.order));
    }

    applyGallery() {
      if (!this.mediaItems || !this.mediaItems.length) return;
      const mode = this.dataset.galleryMode;
      const color = this.colorIdx > -1 ? this.selected[this.colorIdx] : null;
      const group = (color && this.colorGroups[color]) || [];
      const inGroup = (el) => group.includes(Number(el.dataset.mediaId));
      const grouped = new Set(Object.values(this.colorGroups).flat());

      this.mediaItems.forEach((el) => {
        const base = Number(el.dataset.index) + 1;
        el.hidden = false;
        el.classList.remove('is-feature', 'is-solo', 'is-wide');
        if (mode === 'none' || !group.length) { el.style.order = base; return; }
        if (inGroup(el)) el.style.order = group.indexOf(Number(el.dataset.mediaId));
        else el.style.order = base + 100;
        if (mode === 'filter' && !inGroup(el) && grouped.has(Number(el.dataset.mediaId))) el.hidden = true;
      });

      const visible = this.visibleMedia();
      if (visible[0]) visible[0].classList.add('is-feature');
      if (visible.length === 1) visible[0].classList.add('is-solo');
      // Si quedan impares en la rejilla de 2 columnas, la última ocupa todo el ancho
      const featureWide = this.classList.contains('mb-pd--feature') && visible.length > 1;
      const inGrid = featureWide ? visible.length - 1 : visible.length;
      if (visible.length > 1 && inGrid % 2 === 1) visible[visible.length - 1].classList.add('is-wide');
      if (this.total) this.total.textContent = visible.length;
      if (this.counter) this.counter.textContent = '1';
      if (this.mediaList.scrollLeft) this.mediaList.scrollTo({ left: 0, behavior: 'smooth' });
    }

    /* ---------------- Comprar ---------------- */
    bindBuy() {
      if (!this.form) return;
      this.form.addEventListener('submit', (e) => {
        if (e.submitter && e.submitter !== this.addBtn) return; // botones de pago rápido
        e.preventDefault();
        this.add();
      });
    }

    requireSize() {
      const group = this.groups[this.sizeIdx];
      if (!group) return;
      group.classList.remove('is-invalid');
      void group.offsetWidth;
      group.classList.add('is-invalid');
      if (this.avail) {
        this.avail.className = 'mb-pd__avail is-error';
        this.avail.textContent = this.dataset.labelChooseError;
      }
      const r = group.getBoundingClientRect();
      if (r.top < 80 || r.bottom > window.innerHeight) group.scrollIntoView({ behavior: 'smooth', block: 'center' });
      const first = group.querySelector('[data-value]:not(.is-soldout)') || group.querySelector('[data-value]');
      first?.focus({ preventScroll: true });
    }

    async add() {
      if (this.sizeIdx > -1 && !this.selected[this.sizeIdx]) { this.requireSize(); return; }
      const v = this.exactVariant() || this.displayVariant();
      if (!v || !v.available) return;
      if (!window.MatsCart) { this.form.submit(); return; }

      const d = this.dataset;
      this.addBtn?.classList.add('is-loading');
      this.barBtn?.classList.add('is-loading');
      try {
        await window.MatsCart.add(v.id, 1);
        window.MatsCart.announce?.(d.labelAdded);
        if (this.addBtn && this.addLabel) {
          this.addBtn.classList.add('is-added');
          this.addLabel.textContent = d.labelAdded;
          clearTimeout(this._addedT);
          this._addedT = setTimeout(() => { this.addBtn.classList.remove('is-added'); this.update(); }, 1800);
        }
        const mode = d.afterAdd || 'drawer';
        if (mode === 'drawer') setTimeout(() => window.MatsCart.afterAdd('drawer'), 120);
        else if (mode === 'cart') window.MatsCart.afterAdd('cart');
      } catch (err) {
        if (this.avail) {
          this.avail.className = 'mb-pd__avail is-error';
          this.avail.textContent = err.message;
        }
      } finally {
        this.addBtn?.classList.remove('is-loading');
        this.barBtn?.classList.remove('is-loading');
      }
    }

    /* ---------------- Barra fija ---------------- */
    bindBar() {
      this.bar = this.querySelector('[data-mb-pd-bar]');
      if (!this.bar || !this.addBtn) return;
      this.barBtn = this.bar.querySelector('[data-mb-pd-bar-btn]');
      this.barBtn.addEventListener('click', () => {
        if (this.sizeIdx > -1 && !this.selected[this.sizeIdx]) {
          this.requireSize();
          return;
        }
        this.add();
      });
      const io = new IntersectionObserver(([entry]) => {
        const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.bar.classList.toggle('is-visible', show);
        this.bar.setAttribute('aria-hidden', String(!show));
        this.barBtn.tabIndex = show ? 0 : -1;
      });
      io.observe(this.addBtn);
    }

    updateBar(exact, shown, needSize) {
      if (!this.bar) return;
      const d = this.dataset;
      const label = this.bar.querySelector('[data-mb-pd-bar-label]');
      const meta = this.bar.querySelector('[data-mb-pd-bar-meta]');
      const price = this.bar.querySelector('[data-mb-pd-bar-price]');
      const thumb = this.bar.querySelector('[data-mb-pd-bar-thumb]');
      if (label) label.textContent = needSize ? d.labelChoose : (exact && !exact.available ? d.labelSoldout : (this.isPreorder(exact) ? d.labelPreorder : d.labelAdd));
      if (meta) meta.textContent = this.selected.filter((x, i) => x && (i === this.colorIdx || i === this.sizeIdx)).join(' · ');
      if (price) price.textContent = shown.price;
      if (thumb && shown.thumb) thumb.src = shown.thumb;
      if (this.barBtn) this.barBtn.disabled = Boolean(exact && !exact.available);
    }

    /* ---------------- Guía de tallas ---------------- */
    bindGuide() {
      this.guide = this.querySelector('[data-mb-pd-guide]');
      if (!this.guide) return;
      this.guidePick = this.guide.querySelector('[data-mb-pd-guide-pick]');
      this.previewSize = null;

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-mb-pd-guide-open]')) { e.preventDefault(); this.openGuide(); }
      });
      this.guide.querySelector('[data-mb-pd-guide-close]')?.addEventListener('click', () => this.closeGuide());
      this.guide.addEventListener('click', (e) => { if (e.target === this.guide) this.closeGuide(); });

      this.guide.addEventListener('mb-sc:preview', (e) => {
        this.previewSize = e.detail.size;
        this.updateGuidePick();
      });
      this.guidePick?.addEventListener('click', () => {
        if (!this.previewSize) return;
        this.pickSize(this.previewSize);
        this.closeGuide();
      });

      window.addEventListener('mats:size:pick', (e) => {
        if (!e.detail || !e.detail.size) return;
        this.pickSize(e.detail.size);
        if (this.guide.open) this.closeGuide();
      });
    }

    sizeButton(size) {
      const group = this.groups[this.sizeIdx];
      if (!group) return null;
      return Array.from(group.querySelectorAll('[data-value]')).find((b) => norm(b.dataset.value) === norm(size)) || null;
    }

    pickSize(size) {
      const btn = this.sizeButton(size);
      if (btn) this.select(this.sizeIdx, btn.dataset.value, true);
    }

    updateGuidePick() {
      if (!this.guidePick) return;
      const size = this.previewSize;
      const btn = size ? this.sizeButton(size) : null;
      this.guidePick.hidden = !btn;
      if (!btn) return;
      const soldout = btn.classList.contains('is-soldout');
      this.guidePick.disabled = soldout;
      this.guidePick.textContent = soldout ? `${btn.dataset.value} · ${this.dataset.labelSizeSoldout}` : this.dataset.labelPick.replace('[size]', btn.dataset.value);
    }

    openGuide() {
      if (!this.guide) return;
      window.MatsSizeChart?.init(this.guide);
      const size = this.sizeIdx > -1 ? this.selected[this.sizeIdx] : null;
      if (size) window.dispatchEvent(new CustomEvent('mats:size:change', { detail: { size, source: 'product' } }));
      if (typeof this.guide.showModal === 'function') this.guide.showModal();
      else this.guide.setAttribute('open', '');
      document.documentElement.style.overflow = 'hidden';
    }

    closeGuide() {
      if (!this.guide) return;
      if (typeof this.guide.close === 'function') this.guide.close();
      else this.guide.removeAttribute('open');
      document.documentElement.style.overflow = '';
    }

    /* ---------------- Zoom ---------------- */
    bindLightbox() {
      this.lightbox = this.querySelector('[data-mb-pd-lightbox]');
      if (!this.lightbox) return;
      const tpl = this.lightbox.querySelector('[data-mb-pd-lightbox-src]');
      const holder = document.createElement('div');
      holder.appendChild(tpl.content.cloneNode(true));
      this.zoomList = Array.from(holder.querySelectorAll('i')).map((i) => ({ i: Number(i.dataset.i), src: i.dataset.src, alt: i.dataset.alt }));
      this.zoomImg = this.lightbox.querySelector('[data-mb-pd-lightbox-img]');

      this.addEventListener('click', (e) => {
        const z = e.target.closest('[data-mb-pd-zoom]');
        if (!z) return;
        this.openZoom(Number(z.dataset.mbPdZoom));
      });
      this.lightbox.querySelector('[data-mb-pd-lightbox-close]').addEventListener('click', () => this.lightbox.close());
      this.lightbox.querySelector('[data-mb-pd-lightbox-prev]').addEventListener('click', () => this.stepZoom(-1));
      this.lightbox.querySelector('[data-mb-pd-lightbox-next]').addEventListener('click', () => this.stepZoom(1));
      this.lightbox.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowLeft') this.stepZoom(-1);
        if (e.key === 'ArrowRight') this.stepZoom(1);
      });
      this.lightbox.addEventListener('click', (e) => { if (e.target === this.lightbox || e.target === this.zoomImg) this.lightbox.close(); });
      this.lightbox.addEventListener('close', () => { document.documentElement.style.overflow = ''; });
    }

    zoomOrder() {
      const order = this.visibleMedia().map((el) => Number(el.dataset.index));
      return order.map((i) => this.zoomList.find((z) => z.i === i)).filter(Boolean);
    }

    openZoom(index) {
      const list = this.zoomOrder();
      this.zoomPos = Math.max(0, list.findIndex((z) => z.i === index));
      this.showZoom();
      this.lightbox.showModal();
      document.documentElement.style.overflow = 'hidden';
    }

    stepZoom(dir) {
      const list = this.zoomOrder();
      if (!list.length) return;
      this.zoomPos = (this.zoomPos + dir + list.length) % list.length;
      this.showZoom();
    }

    showZoom() {
      const list = this.zoomOrder();
      const z = list[this.zoomPos];
      if (!z) return;
      this.zoomImg.src = z.src;
      this.zoomImg.alt = z.alt;
      const multi = list.length > 1;
      this.lightbox.querySelectorAll('[data-mb-pd-lightbox-prev],[data-mb-pd-lightbox-next]').forEach((b) => { b.hidden = !multi; });
    }

    /* ---------------- Guardados ---------------- */
    bindWishlist() {
      this.saveBtn = this.querySelector('.mb-pd__save[data-mats-wish]');
      if (!this.saveBtn) return;
      const sync = () => {
        const on = Boolean(window.MatsWishlist && window.MatsWishlist.has(this.dataset.handle));
        this.saveBtn.setAttribute('aria-pressed', String(on));
        const label = this.saveBtn.querySelector('[data-mb-pd-save-label]');
        if (label) label.textContent = on ? this.dataset.labelSaved : this.dataset.labelSave;
      };
      document.addEventListener('mats:wishlist:change', () => setTimeout(sync, 0));
      this.saveBtn.addEventListener('click', () => setTimeout(sync, 0));
      if (window.MatsWishlist) sync();
      else window.addEventListener('load', sync, { once: true });
    }

    /* ---------------- Vistos recientemente ---------------- */
    rememberRecent() {
      try {
        const handle = this.dataset.handle;
        let list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        if (!Array.isArray(list)) list = [];
        list = [handle].concat(list.filter((h) => h !== handle)).slice(0, 16);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      } catch (e) { /* noop */ }
    }
  }

  customElements.define('mats-product', MatsProduct);
})();
